"use client";

import { useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useCookies } from "react-cookie";

import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { Hamburger } from "@/components/Hamburger";
import { Wrapper } from "@/components/Wrapper";
import { Logo } from "@/components/Logo";

import { getSiteInfo } from "@/sites";

import { isClient } from "@/utils/browser";
import { paths } from "@/utils/paths";

import { AMPLITUDE_EVENT_NAME } from "@/internals/constants/events";
import { AUTHORIZATION } from "@/internals/constants/cookies";

import { LayoutContext } from "@/contexts/LayoutContext";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";

import { useLogin } from "@/hooks/auth/useLogin";
import { useDomainConfig } from "@/hooks/domain/useDomainConfig";

import { HeaderProps } from "./types";

import "./styles.scss";

const { siteName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);
const { siteBlackLogo28 } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};

export const Header = ({ noNav }: HeaderProps) => {
  const layoutContext = useContext(LayoutContext);
  const { setSource } = useContext(AmplitudeContext);

  const { isUserLoggedIn } = useLogin();
  const { logoLinkEnabled } = useDomainConfig();
  const [cookies] = useCookies();
  const router = useRouter();

  const [isRegisterPage, setIsRegisterPage] = useState(false);
  const [isUserLogged, setIsUserLogged] = useState(false);

  const location = isClient() ? window.location : undefined;

  const navigateTo = (path: string) => {
    router.push(path);
    layoutContext?.setIsMenuOpened(false);
  };

  useEffect(() => {
    setIsRegisterPage(location?.pathname.includes("register"));
    const currentLoginStatus = Boolean(isUserLoggedIn());
    setIsUserLogged(currentLoginStatus);
  }, [location?.pathname]);

  useEffect(() => {
    const hasAuthCookie = Boolean(cookies?.[AUTHORIZATION]);
    setIsUserLogged(hasAuthCookie);
  }, [cookies]);

  const overFlowMenuClass = () => {
    const bodyElement = document.querySelector("body");
    const htmlElement = document.querySelector("html");
    bodyElement.classList.remove("overflowHidden");
    htmlElement.classList.remove("overflowHidden");
  };

  const handleClickMenu = (event, navPath) => {
    overFlowMenuClass();

    if (event) {
      amplitudeTrackEvent(AMPLITUDE_EVENT_NAME[event], {
        source: "navbar",
      });
    }
    navigateTo(navPath);
  };

  return (
    <header
      data-testid="header"
      className="header">
      <Wrapper customClass="header__wrapper">
        <Logo
          href={logoLinkEnabled && "/"}
          src={siteBlackLogo28}
          siteName={siteName}
          dataQa="header-logo-link"
          dataTestId="header-logo"
          className="header__logo"
          setSource={setSource}
        />

        {!noNav && (
          <div className="nav__wrapper">
            <>
              {!isRegisterPage && (
                <Hamburger>
                  {isUserLogged ? (
                    <ul className="header-nav__actions">
                      <li className="header-nav__actions-item">
                        <ButtonLib
                          shape="outlined"
                          label="Go to my account"
                          dataQA="header-dashboard-button"
                          onClick={() => handleClickMenu(undefined, paths.myQrCodes())}
                        />
                      </li>
                    </ul>
                  ) : (
                    <>
                      <ul className="header-nav__actions">
                        <li className="header-nav__actions-item">
                          <ButtonLib
                            shape="ghost"
                            id="login"
                            label="Login"
                            dataQA="header-login-button"
                            onClick={() => handleClickMenu("view_landing_login", paths.login())}
                          />
                        </li>
                        <li className="header-nav__actions-item">
                          <ButtonLib
                            label="Sign up"
                            shape="outlined"
                            dataQA="header-sign-up-button"
                            onClick={() => handleClickMenu("view_landing_signup", paths.signup())}
                          />
                        </li>
                      </ul>
                    </>
                  )}
                </Hamburger>
              )}
            </>
          </div>
        )}
      </Wrapper>
    </header>
  );
};
